import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { edgeFunctionUrl, EdgeFunctions } from '../lib/edgeFunctions';
import { SUPABASE_ANON_KEY } from '../lib/supabaseClient';

interface FormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

function Contact() {
  const { t } = useTranslation(['contact', 'common']);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(edgeFunctionUrl(EdgeFunctions.SEND_CONTACT_FORM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      setFormSubmitted(true);

      // Reset form after submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
        setFormSubmitted(false);
      }, 5000);

    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">{t('contact:hero.title')}</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">{t('contact:hero.subtitle')}</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/3764013/pexels-photo-3764013.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Spa reception" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Contact Form */}
          <div className="w-full lg:w-3/5">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">{t('contact:form.title')}</h2>
            <p className="text-[#232347]/80 mb-8 leading-relaxed">
              {t('contact:form.description')}
            </p>
            
            {formSubmitted ? (
              <div className="bg-[#B2D8C5]/20 border border-[#B2D8C5] text-[#232347] rounded-lg p-6 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-[#B2D8C5] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold mb-2">{t('contact:form.successTitle')}</h3>
                <p>{t('contact:form.successMessage')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white/80 rounded-xl shadow-md p-8">
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
                    <h3 className="font-medium mb-1">Error</h3>
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-[#232347] font-medium mb-2">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[#232347] font-medium mb-2">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                      placeholder="Your email"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-[#232347] font-medium mb-2">Phone (optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="service" className="block text-[#232347] font-medium mb-2">Service of Interest</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5] bg-white"
                    >
                      <option value="">Select a service</option>
                      <option value="massage">Massage Therapies</option>
                      <option value="facial">Facial Treatments</option>
                      <option value="specialty">Specialty Treatments</option>
                      <option value="packages">Spa Packages</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="message" className="block text-[#232347] font-medium mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#B2D8C5] hover:bg-[#88bfa3] text-white font-semibold py-3 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>
          
          {/* Contact Information */}
          <div className="w-full lg:w-2/5">
            <div className="sticky top-24">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">Get In Touch</h2>
              
              {/* Location Card */}
              <div className="bg-white/80 rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-start">
                  <div className="bg-[#F7CAC9]/20 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#232347] mb-2">Our Location</h3>
                    <p className="text-[#232347]/80 mb-1">4222 Trinity Mills Rd Suite 120</p>
                    <p className="text-[#232347]/80">Dallas, TX 75024</p>
                  </div>
                </div>
              </div>
              
              {/* Hours Card */}
              <div className="bg-white/80 rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-start">
                  <div className="bg-[#F7CAC9]/20 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#232347] mb-2">Hours</h3>
                    <div className="grid grid-cols-2 gap-y-2 text-[#232347]/80">
                      <span>Monday - Friday</span>
                      <span>10:00 AM - 7:00 PM</span>
                      <span>Saturday</span>
                      <span>10:00 AM - 7:00 PM</span>
                      <span>Sunday</span>
                      <span>9:30 AM - 7:30 PM</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Phone Card */}
              <div className="bg-white/80 rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-start">
                  <div className="bg-[#F7CAC9]/20 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#232347] mb-2">Phone</h3>
                    <p className="text-[#232347]/80">+1 (972) 998-0431</p>
                  </div>
                </div>
              </div>
              
              {/* Email Card */}
              <div className="bg-white/80 rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-start">
                  <div className="bg-[#F7CAC9]/20 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#232347] mb-2">Email</h3>
                    <p className="text-[#232347]/80">info@healinglotusspa.com</p>
                  </div>
                </div>
              </div>
              
              {/* Social Media */}
              <div className="bg-white/80 rounded-xl shadow-md p-6">
                <h3 className="text-xl font-semibold text-[#232347] mb-4">Connect With Us</h3>
                <div className="flex space-x-4">
                  <a href="#" className="bg-[#F7CAC9]/20 p-3 rounded-full text-[#F7CAC9] hover:bg-[#F7CAC9] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a href="#" className="bg-[#F7CAC9]/20 p-3 rounded-full text-[#F7CAC9] hover:bg-[#F7CAC9] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                    </svg>
                  </a>
                  <a href="#" className="bg-[#F7CAC9]/20 p-3 rounded-full text-[#F7CAC9] hover:bg-[#F7CAC9] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                  </a>
                  <a href="#" className="bg-[#F7CAC9]/20 p-3 rounded-full text-[#F7CAC9] hover:bg-[#F7CAC9] hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Google Map */}
      <div className="w-full h-[400px] mb-16">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3343.5084836697287!2d-96.83058892392145!3d33.05531387357048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c3d4f2b4f3f0f%3A0x1234567890abcdef!2s4222%20Trinity%20Mills%20Rd%20Suite%20120%2C%20Dallas%2C%20TX%2075024!5e0!3m2!1sen!2sus!4v1715649481489!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Healing Lotus Spa Location"
        ></iframe>
      </div>
      
      {/* FAQ Section */}
      <div className="container mx-auto py-16 px-4 md:px-6 mb-16">
        <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">Frequently Asked Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white/80 rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold text-[#232347] mb-3">How do I become a member?</h3>
            <p className="text-[#232347]/80">You can sign up for our Supreme Monthly Membership through our website. Visit the Membership page to learn about exclusive benefits including monthly treatments, 20% discounts, and priority booking.</p>
          </div>
          <div className="bg-white/80 rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold text-[#232347] mb-3">What if I need to cancel my appointment?</h3>
            <p className="text-[#232347]/80">We understand that plans change. We require a 24-hour notice for cancellations to avoid a cancellation fee of 50% of the service price.</p>
          </div>
          <div className="bg-white/80 rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold text-[#232347] mb-3">How early should I arrive for my appointment?</h3>
            <p className="text-[#232347]/80">We recommend arriving 15-20 minutes before your scheduled appointment to complete any necessary paperwork and to allow time to relax and prepare for your treatment.</p>
          </div>
          <div className="bg-white/80 rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold text-[#232347] mb-3">Do you offer memberships?</h3>
            <p className="text-[#232347]/80">Yes, we offer monthly membership programs that provide discounts on services, complimentary enhancements, and exclusive access to member events. Ask our front desk for details.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;